import {
  ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH,
  ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH,
  ALERT_ICON_TYPE_SUCCESS,
  GOOGLE,
  GOOGLE_IMAGE_FORMATS,
} from "../../../../utils/constants";
import { useCreditActionCosts } from "../../../../hooks/useCreditActionCosts";
import Grid from "@mui/material/Grid";
import {
  TITLES,
  DESCRIPTIONS,
  TITLE_PLACEHOLDER,
  DESCRIPTION_PLACEHOLDER,
  IMAGES_OF_CAMPAIGN_PLACEHOLDER,
  VIDEOS_OF_CAMPAIGN_PLACEHOLDER,
  FIELD_MAX_LENGTH,
  FIELD_REQUIRED,
  FIELD_TEXT_REPEATED,
  FIELD_MIN_TEXTS_VALID,
  FIELD_MAX_TEXTS_VALID,
  FIELD_CONSECUTIVE_UPPERCASE,
  FIELD_EMOJI_NOT_ALLOWED,
  ADS_CAMPAIGN_PLATFORM_ASSETS_CONFIGURATION_SUB_TITLE,
  REQUIRED_VIDEOS_GOOGLE_ASSETS_GROUP_HELPER_TEXT,
  COPY_VALUES_IN_OTHER_ADS,
  READY,
} from "../../../../i18n/keysTranslations";
import { Controller, useFormContext } from "react-hook-form";
import useFocusErrorForm from "../../../../hooks/useFocusErrorForm";
import { useTranslationApp } from "../../../../lib/i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import MultiContentField from "../../../Form/MultiContentField";
import Accordion from "../../../Containers/Accordion";
import { getUniqueID } from "../../../../utils/numbers";
import { useDispatchApp } from "../../../../lib/redux";
import { generateAdsCampaignDescription } from "../../../../actions/adsCampaigns";
import {
  BOX_SHADOW_FIELDS,
  INPUT_COLOR_BORDER,
} from "../../../../utils/colors";
import CardContainer from "../../../Card/CardContainer";
import TextFieldWithActions from "../../../Form/TextFieldWithActions";
import { THREE_UPPERCASE_REGEX } from "../../../../utils/regex";
import emojiRegex from "emoji-regex";
import { getIsBlockedActions } from "../../../../actions/getters";
import ContainerVideosYoutube from "../AssetsGroup/ContainerVideosYoutube";
import UploadYoutubeVideoLink from "../AssetsGroup/UploadYoutubeVideoLink";
import ChipOptional from "../../../Chip/ChipOptional";
import ClickableText from "../../../Texts/ClickableText";
import { SimpleAlert } from "../../../Alerts/Alerts";
import { alertIfEmptyCopyValue } from "../../../../utils/copyFormValue";
import {
  commitGeneratedSectionText,
  copySectionValue,
  readStoredSectionFingerprint,
} from "../../../../utils/aiGeneratedForm";
import { toAdsGroupsList } from "../../../../utils/adsGroupsList";
import { useRequiredAdGroupSelection } from "../../../../pages/NewAdsCampaign/hooks/requiredAdsCampaignSelection";
const platform = GOOGLE;
const grid = {
  xs: 12,
  lg: 6,
};

const commonSxAccordion = {
  backgroundColor: "fields.background",
  border: `1px solid ${INPUT_COLOR_BORDER}`,
  boxShadow: BOX_SHADOW_FIELDS,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "stretch",
  minHeight: 70,
};

const emojiRegexInstance = emojiRegex();

function validateNoEmojis(text) {
  if (!text || typeof text !== "string") return true;
  return !emojiRegexInstance.test(text);
}

const AdGroupAppGoogleForm = () => {
  const {
    control,
    formState,
    watch,
    setValue,
    getValues,
    isGeneralDisabledFields,
    trigger,
  } = useFormContext();
  useFocusErrorForm(formState);
  const dispatch = useDispatchApp();

  const errors = formState.errors;
  const { t } = useTranslationApp();
  const { ADS_CAMPAIGN_GENERATE_TEXT_ACTION } = useCreditActionCosts();

  const language = watch("language");
  const platformsData = watch("platformsData") || {};
  const platformData = platformsData?.[platform] || {};
  const isIncompatibleCampaign = platformData?.isIncompatible;
  const { BASIC_PATH } = useRequiredAdGroupSelection();
  const isIncompatibleAdGroup =
    getValues(`${BASIC_PATH}.isIncompatible`) || false;

  const isDisabledFields =
    isGeneralDisabledFields || isIncompatibleCampaign || isIncompatibleAdGroup;

  const titles = watch(`${BASIC_PATH}.titles`) || [
    { value: "", id: getUniqueID() },
    { value: "", id: getUniqueID() },
  ];
  const descriptions = watch(`${BASIC_PATH}.descriptions`) || [
    { value: "", id: getUniqueID() },
  ];

  const onAddText = ({ type }) => {
    const currentTexts = watch(`${BASIC_PATH}.${type}`) || [];
    setValue(`${BASIC_PATH}.${type}`, [
      ...currentTexts,
      { value: "", id: getUniqueID() },
    ]);
  };

  const onRemoveText = ({ type, index }) => {
    const currentTexts = watch(`${BASIC_PATH}.${type}`);
    setValue(
      `${BASIC_PATH}.${type}`,
      currentTexts.filter((_, i) => i !== index),
      { shouldValidate: true, shouldDirty: true }
    );
    trigger(`${BASIC_PATH}.${type}`);
  };

  const onGenerateText = async ({ type, index }) => {
    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_GENERATE_TEXT_ACTION,
        showUsageConfirmationAlert: true,
      })
    );
    if (isBlocked) return;

    const currentTexts = getValues(`${BASIC_PATH}.${type}`) || [];
    const currentText = currentTexts[index]?.value || "";
    const allTexts = currentTexts.map((item) => item.value || "");
    const textsWithCurrentFirst = [
      currentText,
      ...allTexts.filter((_, i) => i !== index),
    ];

    const objective = getValues(`platformsData.${platform}.objective`);

    const response = await dispatch(
      generateAdsCampaignDescription({
        campaignID: getValues("id"),
        objective,
        platform,
        campaignContext: getValues("context") || "",
        language: getValues("language"),
        destinationUrl: getValues(`${BASIC_PATH}.destinationUrl`) || "",
        type,
        currentTexts: textsWithCurrentFirst,
        entityType: "adGroup",
        currentFingerprint: readStoredSectionFingerprint({
          getValues,
          basePath: BASIC_PATH,
          section: type,
        }),
      })
    );

    commitGeneratedSectionText({
      getValues,
      setValue,
      trigger,
      basePath: BASIC_PATH,
      section: type,
      index,
      platform,
      entityType: "adGroup",
      response,
      triggerPath: `${BASIC_PATH}.${type}`,
    });
  };

  const maxTitles = 5;
  const minTitles = 2;
  const maxDescriptions = 5;
  const minDescriptions = 1;
  const maxImages = 20;
  const maxVideos = 20;

  const onCopyValuesInOtherAds = ({ type }) => {
    const value = getValues(`${BASIC_PATH}.${type}`);
    if (alertIfEmptyCopyValue({ value, t })) return;
    const adsGroupsValues = toAdsGroupsList(getValues("adsGroups"));

    adsGroupsValues.forEach((adGroup, currentIndexAdGroup) => {
      if (adGroup.platform !== platform) return;

      copySectionValue({
        setValue,
        getValues,
        sourceBasePath: BASIC_PATH,
        section: type,
        platform,
        entityType: "adGroup",
        path: `adsGroups.[${currentIndexAdGroup}].${type}`,
        value,
      });
    });

    SimpleAlert({
      title: t(READY),
      icon: ALERT_ICON_TYPE_SUCCESS,
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        width: "100%",
      }}
    >
      <Grid container spacing={2}>
        {/* Media Configuration */}
        <Grid item xs={12}>
          <CardContainer
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t(ADS_CAMPAIGN_PLATFORM_ASSETS_CONFIGURATION_SUB_TITLE)}
                </Typography>
                <ChipOptional size="small" />
              </Box>
            }
          >
            <Grid container spacing={2}>
              <Grid item {...grid}>
                <Accordion
                  title={t(IMAGES_OF_CAMPAIGN_PLACEHOLDER)}
                  sx={{
                    ...commonSxAccordion,
                  }}
                  name={`${BASIC_PATH}.imageUrls`}
                  id={`${BASIC_PATH}.imageUrls`}
                  errors={errors}
                  showError={true}
                >
                  <ClickableText
                    disabled={isDisabledFields}
                    onClick={() =>
                      onCopyValuesInOtherAds({ type: "imageUrls" })
                    }
                  >
                    {t(COPY_VALUES_IN_OTHER_ADS)}
                  </ClickableText>
                  <Controller
                    control={control}
                    name={`${BASIC_PATH}.imageUrls`}
                    id={`${BASIC_PATH}.imageUrls`}
                    render={({ field }) => (
                      <MultiContentField
                        platform={GOOGLE}
                        language={language}
                        showHoverPreview={true}
                        disabled={isDisabledFields}
                        acceptedFormats={GOOGLE_IMAGE_FORMATS}
                        mediaUrls={field.value}
                        showGalleryGoogleOption={true}
                        propsPostsAdsPlatforms={platformsData}
                        onChange={(contents) => {
                          const newImageUrls = contents.map(
                            (content) => content.url
                          );
                          const newValues =
                            getValues(`${BASIC_PATH}.imageUrls`) || [];
                          const totalImages =
                            newValues.length + newImageUrls.length;
                          if (totalImages > maxImages) {
                            return;
                          }
                          field.onChange([...newValues, ...newImageUrls]);
                        }}
                        onDelete={(index) => {
                          field.onChange(
                            field.value.filter((_, i) => i !== index)
                          );
                        }}
                      />
                    )}
                    rules={{
                      validate: (value) => {
                        if (!value || value.length === 0) return true;
                        if (value.length > maxImages) {
                          return t(FIELD_MAX_TEXTS_VALID, { value: maxImages });
                        }
                        return true;
                      },
                    }}
                  />
                </Accordion>
              </Grid>
              <Grid item {...grid}>
                <Accordion
                  title={t(VIDEOS_OF_CAMPAIGN_PLACEHOLDER)}
                  sx={{
                    ...commonSxAccordion,
                  }}
                  name={`${BASIC_PATH}.videoUrls`}
                  id={`${BASIC_PATH}.videoUrls`}
                  errors={errors}
                  showError={true}
                >
                  <ClickableText
                    disabled={isDisabledFields}
                    onClick={() =>
                      onCopyValuesInOtherAds({ type: "videoUrls" })
                    }
                  >
                    {t(COPY_VALUES_IN_OTHER_ADS)}
                  </ClickableText>
                  <Typography
                    variant="body2"
                    gutterBottom
                    dangerouslySetInnerHTML={{
                      __html: t(
                        REQUIRED_VIDEOS_GOOGLE_ASSETS_GROUP_HELPER_TEXT
                      ),
                    }}
                    sx={{
                      my: 1,
                    }}
                  />
                  <UploadYoutubeVideoLink
                    name={`${BASIC_PATH}.videoUrls`}
                    disabled={isDisabledFields}
                    language={language}
                    maxVideos={maxVideos}
                  />
                  <Controller
                    control={control}
                    name={`${BASIC_PATH}.videoUrls`}
                    rules={{
                      validate: (value) => {
                        if (!value || value.length === 0) return true;
                        if (value.length > maxVideos) {
                          return t(FIELD_MAX_TEXTS_VALID, { value: maxVideos });
                        }
                        return true;
                      },
                    }}
                    render={({ field }) => {
                      const currentVideos = field.value || [];
                      // Limit to maxVideos
                      if (currentVideos.length > maxVideos) {
                        const limited = currentVideos.slice(0, maxVideos);
                        field.onChange(limited);
                      }
                      return (
                        <ContainerVideosYoutube
                          name={`${BASIC_PATH}.videoUrls`}
                          disabled={isDisabledFields}
                          maxVideos={maxVideos}
                          showUrlInput={false}
                        />
                      );
                    }}
                  />
                </Accordion>
              </Grid>
            </Grid>
          </CardContainer>
        </Grid>

        {/* Titles */}
        <Grid item xs={12} sm={6} id={`${BASIC_PATH}.titles`}>
          <CardContainer
            aiGeneratedSection={{
              basePath: BASIC_PATH,
              section: "titles",
            }}
            title={
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t(TITLES)}
                </Typography>
                <ClickableText
                  disabled={isDisabledFields}
                  onClick={() => onCopyValuesInOtherAds({ type: "titles" })}
                >
                  {t(COPY_VALUES_IN_OTHER_ADS)}
                </ClickableText>
              </Box>
            }
          >
            <Controller
              control={control}
              name={`${BASIC_PATH}.titles`}
              rules={{
                validate: (value) => {
                  const validTitles = (value || []).filter(
                    (item) => item?.value && item.value.trim().length > 0
                  );
                  if (validTitles.length < minTitles) {
                    return t(FIELD_MIN_TEXTS_VALID, { value: minTitles });
                  }
                  if (validTitles.length > maxTitles) {
                    return t(FIELD_MAX_TEXTS_VALID, { value: maxTitles });
                  }
                  return true;
                },
              }}
              render={() => (
                <>
                  {titles.map((title, index) => (
                    <TextFieldWithActions
                      key={title.id}
                      labelTitle={t(TITLE_PLACEHOLDER) + " " + (index + 1)}
                      type="titles"
                      name={`${BASIC_PATH}.titles.[${index}].value`}
                      disabled={isDisabledFields}
                      showAddIcon={
                        index === titles.length - 1 && index < maxTitles - 1
                      }
                      showDeleteIcon={titles.length > minTitles}
                      platform={platform}
                      onAdd={() => onAddText({ type: "titles" })}
                      onRemove={() => onRemoveText({ type: "titles", index })}
                      maxLength={
                        ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[platform]
                      }
                      index={index}
                      onGenerate={async () => {
                        await onGenerateText({ type: "titles", index });
                      }}
                      control={control}
                      errors={errors}
                      rules={{
                        required: {
                          value: true,
                          message: t(FIELD_REQUIRED),
                        },
                        validate: (value) => {
                          if (!value || value.trim().length === 0) {
                            return t(FIELD_REQUIRED);
                          }
                          if (
                            value.length >
                            ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[platform]
                          ) {
                            return t(FIELD_MAX_LENGTH, {
                              value:
                                ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[
                                  platform
                                ],
                            });
                          }
                          if (THREE_UPPERCASE_REGEX.test(value)) {
                            return t(FIELD_CONSECUTIVE_UPPERCASE, { value: 3 });
                          }
                          if (!validateNoEmojis(value)) {
                            return t(FIELD_EMOJI_NOT_ALLOWED);
                          }
                          const allTitles = titles.map((item) =>
                            (item.value || "").trim().toLowerCase()
                          );
                          const timesRepeated = allTitles.filter(
                            (text) => text === value.trim().toLowerCase()
                          ).length;
                          if (timesRepeated > 1) {
                            return t(FIELD_TEXT_REPEATED);
                          }
                          return true;
                        },
                      }}
                    />
                  ))}
                </>
              )}
            />
          </CardContainer>
        </Grid>

        {/* Descriptions */}
        <Grid item xs={12} sm={6} id={`${BASIC_PATH}.descriptions`}>
          <CardContainer
            aiGeneratedSection={{
              basePath: BASIC_PATH,
              section: "descriptions",
            }}
            title={
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t(DESCRIPTIONS)}
                </Typography>
                <ClickableText
                  disabled={isDisabledFields}
                  onClick={() =>
                    onCopyValuesInOtherAds({ type: "descriptions" })
                  }
                >
                  {t(COPY_VALUES_IN_OTHER_ADS)}
                </ClickableText>
              </Box>
            }
          >
            <Controller
              control={control}
              name={`${BASIC_PATH}.descriptions`}
              rules={{
                validate: (value) => {
                  const validDescriptions = (value || []).filter(
                    (item) => item?.value && item.value.trim().length > 0
                  );
                  if (validDescriptions.length < minDescriptions) {
                    return t(FIELD_MIN_TEXTS_VALID, { value: minDescriptions });
                  }
                  if (validDescriptions.length > maxDescriptions) {
                    return t(FIELD_MAX_TEXTS_VALID, { value: maxDescriptions });
                  }
                  return true;
                },
              }}
              render={() => (
                <>
                  {descriptions.map((description, index) => (
                    <TextFieldWithActions
                      key={description.id}
                      labelTitle={
                        t(DESCRIPTION_PLACEHOLDER) + " " + (index + 1)
                      }
                      type="descriptions"
                      name={`${BASIC_PATH}.descriptions.[${index}].value`}
                      disabled={isDisabledFields}
                      showAddIcon={
                        index === descriptions.length - 1 &&
                        index < maxDescriptions - 1
                      }
                      showDeleteIcon={descriptions.length > minDescriptions}
                      platform={platform}
                      onAdd={() => onAddText({ type: "descriptions" })}
                      onRemove={() =>
                        onRemoveText({ type: "descriptions", index })
                      }
                      maxLength={
                        ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[platform]
                      }
                      index={index}
                      onGenerate={async () => {
                        await onGenerateText({ type: "descriptions", index });
                      }}
                      control={control}
                      errors={errors}
                      rules={{
                        required: {
                          value: true,
                          message: t(FIELD_REQUIRED),
                        },
                        validate: (value) => {
                          if (!value || value.trim().length === 0) {
                            return t(FIELD_REQUIRED);
                          }
                          if (
                            value.length >
                            ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[
                              platform
                            ]
                          ) {
                            return t(FIELD_MAX_LENGTH, {
                              value:
                                ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[
                                  platform
                                ],
                            });
                          }
                          if (!validateNoEmojis(value)) {
                            return t(FIELD_EMOJI_NOT_ALLOWED);
                          }
                          const allDescriptions = descriptions.map((item) =>
                            (item.value || "").trim().toLowerCase()
                          );
                          const timesRepeated = allDescriptions.filter(
                            (text) => text === value.trim().toLowerCase()
                          ).length;
                          if (timesRepeated > 1) {
                            return t(FIELD_TEXT_REPEATED);
                          }
                          return true;
                        },
                      }}
                    />
                  ))}
                </>
              )}
            />
          </CardContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdGroupAppGoogleForm;
